import { Button, Card, HSpacer, Text, TextBadge } from '@/components/DesignSystem';
import {
  getProgramPeriodText,
} from '@/pages/Admin/HierarchyOfRetailers/Retailer/RewardsPrograms/helpers';
import { ApiRewardsProgram } from '@api/interfaces/ApiRewardsProgram';
import EditOutlined from '@mui/icons-material/EditOutlined';
import GroupAddOutlined from '@mui/icons-material/GroupAddOutlined';
import TuneOutlined from '@mui/icons-material/TuneOutlined';
import { Stack, useTheme } from '@mui/material';
import { ProgramStatus } from '@shared/enums/ProgramStatus';
import { getProgramStatus } from '@shared/utilities';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';

interface EnergyProgramRowProps {
  onEdit: () => void,
  onInitializeCustomers?: () => void,
  onManageBehaviors?: () => void,
  program: ApiRewardsProgram,
}

export const EnergyProgramRow = ({
  onEdit,
  onInitializeCustomers,
  onManageBehaviors,
  program,
}: EnergyProgramRowProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const status = getProgramStatus(program);

  let statusColor;
  let statusTextColor = undefined;
  switch (status) {
    case ProgramStatus.Active:
      statusColor = theme.palette.success.main;
      break;
    case ProgramStatus.Future:
      statusColor = theme.palette.info.main;
      break;
    case ProgramStatus.Expired:
      statusColor = theme.palette.outline;
      statusTextColor = theme.palette.text.primary;
      break;
    default:
      statusColor = undefined;
  }

  const createdDateText = DateTime.fromJSDate(program.createdAt).toFormat('MMM d, yyyy');

  return (
    <Card
      cardContentStyle={{ '&:last-child': { paddingBottom: 0 }, padding: 0 }}
      elevation={0}
      testID={`energy-program-card-${program.id}`}
    >
      <Stack
        alignItems="center"
        direction="row"
        justifyContent="space-between"
        sx={{ p: '16px 0px 16px 16px', height: '52px', width: '100%' }}
      >
        <Stack alignItems="center" direction="row">
          <Stack alignItems="center" direction="row" width="100px">
            {!!status && (
              <TextBadge backgroundColor={statusColor} text={status} textColor={statusTextColor} />
            )}
          </Stack>
          <HSpacer size="3" />
          <Stack alignItems="center" direction="row" width="283px">
            <Text sx={{ color: theme.palette.text.secondary }}>
              Program period:
            </Text>
            <HSpacer size="2" />
            <Text>
              {getProgramPeriodText(program)}
            </Text>
          </Stack>
          <HSpacer size="8" />
          <Text sx={{ color: theme.palette.text.secondary }}>
            Created:
          </Text>
          <HSpacer size="2" />
          <Text>
            {createdDateText}
          </Text>
        </Stack>
        <Stack direction="row">
          {status !== ProgramStatus.Expired && !!onManageBehaviors && (
            <Button
              onClick={onManageBehaviors}
              startIcon={<TuneOutlined />}
              testID={`energy-program-manage-behaviors-${program.id}`}
              variant="text"
            >
              {t(($) => $.energy.row.manageBehaviors)}
            </Button>
          )}
          {status === ProgramStatus.Active && !!onInitializeCustomers && (
            <Button
              onClick={onInitializeCustomers}
              startIcon={<GroupAddOutlined />}
              testID={`energy-program-initialize-${program.id}`}
              variant="text"
            >
              {t(($) => $.energy.row.initializeCustomers)}
            </Button>
          )}
          {status !== ProgramStatus.Expired && (
            <Button
              onClick={onEdit}
              startIcon={<EditOutlined />}
              testID={`energy-program-edit-button-${program.id}`}
              variant="text"
            >
              Edit
            </Button>
          )}
        </Stack>
      </Stack>
    </Card>
  );
};
